// eslint-disable-next-line no-unused-vars
import React, { memo, useMemo, useState } from 'react';
import { createContext } from 'use-context-selector';
import { node } from 'prop-types';
import { stringify } from 'query-string';
import omit from 'lodash/omit';

import useRouter from 'Hooks/useRouter';
import useProducts from 'Hooks/useProducts';
import useCategories from 'Hooks/useCategories';

export const FiltersContext = createContext({ data: {}, error: null });

const nonAttrs = ['category', 'brands', 'price'];

const FiltersProvider = memo(({ children }) => {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { push, location, query } = useRouter();
  // eslint-disable-next-line no-unused-vars
  const { activeCategory } = useCategories();
  const { data } = useProducts();
  const filters = data?.filters || {};

  /**
   * Gets original filter data from path-parsed filter key/value pair
   * @param {string} key
   * @param {string|array} value
   * @returns {object|null}
   */
  const getFilterData = (key, value) => {
    if (key === 'category') {
      if (activeCategory?.id === value?.id) {
        return activeCategory;
      }
      const foundChildCat = activeCategory?.children?.find(i => i.id === value?.id);
      if (foundChildCat) {
        return foundChildCat;
      }
    } else if (key === 'brands') {
      const results = (filters.manufacturers || []).filter(brand => value.find(v => v.id === brand.id));
      if (results.length) {
        return results;
      }
    } else if (filters.properties) {
      const originalKey = key.replace(/-+(\d+)$/, '_$1');
      const filter = filters.properties.find(f => f.slug === originalKey);
      if (filter) {
        return (filter.values || []).filter(attr => value.find(v => v.valueId === attr.valueId));
      }
    }

    return value;
  };

  const groupPattern = `\\d+\\w+\\_\\,\\&\\-'\\"+\\=`;

  /**
   * Filters path looks like this
   *  /products/categoryName-cId/brands/[brandName-bId]/attributes/attrName-nId,[attrValue-vId]/e/price/min,max/search/saerchTerm/c
   * We need to properly parse it in order to get an object containing active filters
   * { category: id, brands: [id], price: [min, max], attributes: { [string]: [id] } }
   * @param {string} path
   * @returns { object }
   */
  const getFiltersFromPath = path => {
    if (!path.replace(/^\/?products\/?$/, '').trim().length) return null;

    const pathFilters = {};

    const catPattern = new RegExp(`^\\/products\\/([${groupPattern}]*)(-c\\d+)\\/?`);
    const categoryMatch = path.match(catPattern);
    if (categoryMatch && categoryMatch[2]) {
      const [, name, id] = categoryMatch;
      const splitName = name.split('-');
      pathFilters.category = {
        name: splitName
          .map(v => v.replace(/\w{1}/, v.charAt(0).toUpperCase()))
          .reduce((acc, word, idx) => {
            if (idx === splitName.length - 1) {
              // eslint-disable-next-line no-param-reassign
              acc += word;
            } else if (idx === splitName.length - 2) {
              // eslint-disable-next-line no-param-reassign
              acc += `${word} & `;
            } else {
              // eslint-disable-next-line no-param-reassign
              acc += `${word}, `;
            }
            return acc;
          }, ''),
        slug: name,
        id: parseInt(id.replace(/^-c/, ''), 10)
      };
    }

    if (path.includes('brands')) {
      const brandsPattern = new RegExp(`\\/brands\\/([${groupPattern}]*)\\/?`);
      const brandsMatch = path.match(brandsPattern);
      if (brandsMatch?.length > 0) {
        pathFilters.brands = brandsMatch[1]
          .split(',')
          .map(brand => {
            const [name, id] = brand.split(/-b(\w+)$/);
            return {
              id,
              name: name.toUpperCase(),
              slug: name,
              count: 0
            };
          });
      }
    }

    if (path.includes('price')) {
      const priceMatch = path.match(/\/price\/([\d+.,]*)\/?/);
      if (priceMatch?.length > 0) {
        pathFilters.price = priceMatch[1]
          .split(',')
          .map(price => Math.floor(parseInt(price, 10)))
          .sort((a, b) => a - b);
      }
    }

    if (path.includes('search')) {
      const searchMatch = path.match(/\/search\/([\s\d\w+=\-_"'%\.,]*)\/?/); // eslint-disable-line
      if (searchMatch?.length > 0) {
        pathFilters.search = searchMatch[1].split(/\s|%20/);
      }
    }

    if (path.includes('attributes')) {
      const attributesPattern = new RegExp(`\\/attributes\\/([${groupPattern}\\/]*)\\/e\\/?`);
      const attributesMatch = path.match(attributesPattern);
      if (attributesMatch && attributesMatch[1]) {
        pathFilters.attributes = {};
        attributesMatch[1]
          .split('/')
          .forEach(groups => {
            const [key, ...values] = groups.split(',');
            const id = key.split('-').at(-1).replace('n', '');
            const newKey = `${key.replace(/-n\d+$/, '')}_${id}`;
            pathFilters.attributes[newKey] = values
              .map(v => {
                const [value, valueId] = v.split(/-v(\d+)$/);
                const slug = `${value}_${valueId}`;
                return {
                  count: 0,
                  valueId: parseInt(valueId, 10),
                  value: value.replace(/\w{1}/, value.charAt(0).toUpperCase()),
                  slug
                };
              });
          });
      }
    }

    return Object
      .entries(pathFilters)
      .reduce((acc, [key, filter]) => {
        if (key === 'attributes') {
          Object.entries(filter).forEach(([filterKey, filterValues]) => {
            acc[filterKey] = getFilterData(filterKey, filterValues);
          });
        } else {
          acc[key] = getFilterData(key, filter);
        }
        return acc;
      }, {});
  };

  /**
   * Compose attributes
   * @param key {string}
   * @param value {object}
   * @returns {string|null}
   */
  const composeAttributePathPart = (key, value) => {
    // assume filter properties as default
    const filter = filters.properties.find(attr => attr.slug === key);
    if (!filter) {
      console.warn(`FILTERS: No filter found for key: %c ${key}`, 'color: red');
    }

    const values = Array.isArray(value) ? value : [value];
    const attrName = key.replace(/_+(\d+)$/, '-n$1');
    const attrValues = values.reduce((acc, item) => {
      const valueName = item.slug.replace(/_+(\d+)$/, '-v$1');
      acc.push(valueName);
      return acc;
    }, []).join(',');
    return `/${ attrName },${ attrValues }`;
  };

  /**
   * Composes filter part for path
   * @param {string} key
   * @param {any} value
   * @returns {string|*}
   */
  const composePathPart = (key, value) => {
    switch (key) {
      case 'category':
        return `/${ value.slug }-c${ value.id }`;
      case 'brands':
        return `/brands/${ value.reduce((acc, item) => {
          acc.push(`${ item.slug }-b${ item.id }`);
          return acc;
        }, []).join(',') }`;
      case 'price':
        /* eslint-disable no-restricted-globals */
        return `/price/${ !isNaN(value[0]) ? Math.floor(value[0]) : '' },${ !isNaN(value[1]) ? Math.floor(value[1]) : '' }`;
      /* eslint-enable no-restricted-globals */
      case 'search':
        return `/search/${ value.join(' ') }`;
      default:
        console.warn(`FILTERS: Key %c ${key} not registered !`, 'color: red');
        return null;
    }
  };

  /**
   * Creates path from active filters
   * @param {object} items
   * @return string
   */
  const composePathFromFilters = items => {
    const sortedKeys = Object
      .keys(items)
      .sort((a, b) => {
        if (b === 'category') return 1;
        if (b === 'brands' && a !== 'category') return 1;
        return -1;
      });

    const { attr: attrKeys, nonAttr: nonAttrKeys, search: searchKeys } = sortedKeys.reduce((acc, key) => {
      if (nonAttrs.includes(key)) {
        acc.nonAttr.push(key);
      } else if (key === 'search') {
        acc.search.push(key);
      } else {
        acc.attr.push(key);
      }
      return acc;
    }, { attr: [], nonAttr: [], search: [] });

    const attrPath = attrKeys.reduce((acc, key) => {
      const value = items[key];
      if (
        !value
        || (Array.isArray(value) && !value.length)
      ) return acc;
      const path = composeAttributePathPart(key, value);
      if (!path) return acc;
      acc.push(path);
      return acc;
    }, ['/attributes']).join('');

    const nonAttrPath = nonAttrKeys.reduce((acc, key) => {
      const value = items[key];
      if (
        !value
        || (Array.isArray(value) && !value.length)
      ) return acc;

      const path = composePathPart(key, value);
      if (!path) return acc;
      acc.push(path);
      return acc;
    }, []).join('');

    const searchPath = searchKeys.reduce((acc, key) => {
      const value = items[key];
      if (
        !value
        || (Array.isArray(value) && !value.length)
      ) return acc;

      const path = composePathPart(key, value);
      if (!path) return acc;
      acc.push(path);
      return acc;
    }, []).join('');

    return `${nonAttrPath}${attrPath !== '/attributes' ? `${attrPath}/e` : ''}${searchPath !== '/search' ? `${searchPath}` : ''}`;
  };

  const memoizedFilters = useMemo(
    () => {
      const activeFilters = getFiltersFromPath(location.pathname) || {};

      let category = {};
      if (
        Object.entries(activeCategory || {}).length
        && activeCategory.id === (activeFilters.category?.id || activeFilters.category)
      ) {
        category = activeCategory;
      } else if (activeFilters.category) {
        if (typeof activeFilters.category === 'number') {
          category = { id: activeFilters.category };
        } else {
          category = activeFilters.category;
        }
      }
      return {
        data: filters,
        activeFilters: {
          ...activeFilters,
          category
        },
        updateFilters: payload => {
          const newActiveFilters = {
            ...activeFilters,
            ...payload
          };
          const filtersPath = composePathFromFilters(
            Object
              .entries(newActiveFilters)
              .reduce((acc, [key, value]) => {
                if (
                  Array.isArray(value)
                    ? !value.length
                    : !value
                ) {
                  delete acc[key];
                }
                return acc;
              }, newActiveFilters)
          );
          const currentPath = location.pathname.replace(/^\/?(products)/, '').replace(/\/c$/ig, '');

          if (filtersPath !== currentPath) {
            push({
              pathname: filtersPath.length ? `/products${ filtersPath }/c` : '/products',
              search: stringify(omit(query, ['page']))
            });
          }
        },
        removeFilter: (key, value) => {
          const activeFilter = activeFilters[key];
          if (Array.isArray(activeFilter)) {
            activeFilter.splice(activeFilter.indexOf(value), 1);
          }
          const filtersPath = composePathFromFilters(activeFilters);
          push({
            pathname: filtersPath.length ? `/products/${ filtersPath }/c` : '/products',
            search: stringify(omit(query, ['page']))
          });
        },
        clearFilters: () => {
          const categoryToUse = activeFilters.category || activeCategory;
          const hasCategoryData = categoryToUse && categoryToUse.slug && categoryToUse.id;
          push({
            pathname: `/products${hasCategoryData ? `/${categoryToUse.slug}-c${categoryToUse.id}/c` : ''}`,
            search: stringify(omit(query, [
              'page',
              'only_promotional',
              'only_available',
              'only_resealed',
              'only_warehouse_products',
              'include_reserved_stock',
              'stock-clearing-sale'
            ]))
          });
        },
        mobileFiltersOpen,
        setMobileFiltersOpen,
      };
    },
    [
      activeCategory,
      filters,
      location.pathname,
      query,
      mobileFiltersOpen,
      push,
    ]
  );

  return (
    <FiltersContext.Provider value={ memoizedFilters }>
      { children }
    </FiltersContext.Provider>
  );
});

FiltersProvider.propTypes = {
  children: node
};

export default FiltersProvider;
